import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * IntakeRouteGuard — upload-route guard for /document-upload-hub and /upload
 *
 * Public medical-record intake is allowed (guest or signed-in).
 * Only an explicit intake stop (enabled=false or submission_stop_active)
 * blocks the form. Upload permission is no longer inferred from a hardcoded
 * document_bytes_permitted=false flag.
 */
const IntakeRouteGuard = ({ children }) => {
  const [state, setState] = useState('loading'); // loading | permitted | stopped
  const [detail, setDetail] = useState('');

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const { supabase } = await import('../lib/supabase');
        const { data, error } = await supabase?.rpc('intake_runtime_public_status');
        if (cancelled) return;

        if (error) {
          // Fail open for the form: storage/RLS still enforce writes.
          setState('permitted');
          return;
        }

        if (data?.enabled === false || data?.submission_stop_active === true) {
          setDetail('New medical-record submissions are temporarily paused. Please try again later or contact info@curegate.ai.');
          setState('stopped');
          return;
        }

        setState('permitted');
      } catch {
        if (!cancelled) setState('permitted');
      }
    };

    check();
    return () => { cancelled = true; };
  }, []);

  if (state === 'loading') {
    return (
      <div className="min-h-screen px-4 py-16" style={{ background: '#0A0A0C' }} aria-busy="true" aria-label="Loading" data-testid="intake-status-skeleton">
        <div className="max-w-2xl mx-auto space-y-4">
          <div className="h-8 w-2/3 mx-auto rounded-lg bg-white/10 animate-pulse" />
          <div className="h-4 w-1/2 mx-auto rounded bg-white/5 animate-pulse" />
          <div className="h-48 rounded-2xl bg-white/5 animate-pulse" />
          <div className="h-28 rounded-2xl bg-white/5 animate-pulse" />
        </div>
      </div>
    );
  }

  if (state === 'stopped') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0A0C' }}>
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-xl font-semibold text-white">Uploads paused</h1>
          <p className="text-sm text-gray-400 leading-relaxed">{detail}</p>
          <Link to="/" className="inline-block px-4 py-2 rounded-lg bg-violet-700 text-white text-sm">
            Return home
          </Link>
        </div>
      </div>
    );
  }

  return children;
};

export default IntakeRouteGuard;
